import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskedLines } from "../Reveal";
import { trackEvent } from "../../lib/analytics";

export const Hero = ({ data }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  // Subtle parallax: background drifts slower, content fades/moves
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.1, 1.25]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  const hero = data?.hero || {};

  return (
    <section
      id="hero"
      ref={ref}
      className="relative h-screen min-h-[700px] w-full overflow-hidden"
      data-testid="hero-section"
    >
      {/* Parallax background */}
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 z-0">
        <img
          src={hero.background_image}
          alt="Luxury bar atmosphere"
          className="h-full w-full object-cover object-left md:object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-void/60 via-void/45 to-void" />
      </motion.div>

      {/* Overline */}
      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 h-full max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col justify-center"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 1 }}
          className="mb-6 flex items-center gap-4"
        >
          <span className="h-px w-12 bg-gold" />
          <span className="text-gold text-xs uppercase tracking-[0.3em]">
            Beverage Boutique Agency
          </span>
        </motion.div>

        {/* Kinetic masked headline */}
        <h1 className="font-display font-semibold tracking-tight leading-[0.95] text-[13vw] sm:text-[10vw] lg:text-[7.5rem]">
          <MaskedLines
            lines={[hero.line1 || "Crafting Experiences,"]}
            lineClassName="text-gold italic"
            startDelay={0.5}
          />
          <MaskedLines
            lines={[hero.line2 || "One Pour at a Time"]}
            lineClassName="text-ink"
            startDelay={0.68}
          />
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9 }}
          className="mt-8 max-w-xl"
        >
          <p className="text-smoke text-base md:text-lg leading-relaxed">
            {hero.subtitle}
          </p>
          <p className="mt-2 text-coral font-medium">{hero.accent}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.9 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <a
            href="#services"
            data-scroll-to="services"
            data-testid="hero-discover-btn"
            onClick={() => trackEvent("cta_click", { cta: "discover_more", section: "hero" })}
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-semibold uppercase tracking-widest text-black transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_10px_40px_-10px_rgba(255,200,0,0.6)]"
          >
            Discover More
          </a>
          <a
            href="#contact"
            data-scroll-to="contact"
            data-testid="hero-contact-btn"
            onClick={() => trackEvent("cta_click", { cta: "contact_us", section: "hero" })}
            className="group inline-flex items-center justify-center gap-2 rounded-full border border-coral px-8 py-4 text-sm font-semibold uppercase tracking-widest text-coral transition-all duration-300 hover:-translate-y-0.5 hover:bg-coral hover:text-white"
          >
            Contact Us
          </a>
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.a
        href="#about"
        data-scroll-to="about"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-gold"
        aria-label="Scroll down"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
        >
          <ArrowDown size={28} />
        </motion.div>
      </motion.a>
    </section>
  );
};
